import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import { LienDataType } from "../../../Types/lienProcessingTypes";
import styles from "./LienDetailedView.module.scss";
import type { ColumnsType } from "antd/es/table";
import jpegIcon from "../../../assets/images/jpeg-image-icon.svg";
import { getPrevDollorFormattedString } from "../../../Utilities";
import clsx from "clsx";
import { message } from "antd";

interface ProjectDetailsContent {
  tier1?: string;
  tier2?: string;
  tier3?: string;
}

interface ProjectSummaryContent {
  customer?: string;
  jobName?: string;
  totalAmount?: string;
  totalOpenBalance?: string;
  previousStage?: string;
  nextStage?: string;
  filingDate?: string;
  notifiedTo?: string;
}

export const componentConstants = {
  FIELD: {
    SEARCH_NAME: "search",
    SEARCH_PLACEHOLDER: "Search",
    SEARCH_TYPE: InputfieldTypes.TEXT,
    SEARCH_ICON_ALT: "Click to search",
  },
  BUTTON: {
    FORMS_BUTTON_LABEL: "Show recommended forms",
  },
  HEADERS: {
    PROJECT_DETAILS: "Project Details",
    PROJECT_SUMMARY: "Project Summary",
  },
  MODAL: {
    MODAL_WIDTH: "716px",
    DOWNLOAD_LABEL: "Download",
  },
  DOCUMENTS_GENERATING_MESSAGE: "Documents are not generated yet. Please try again later",
};

export const DOCUMENT_GENERATION_STATUS = {
  NOT_QUEUED: "NOT_QUEUED",
  QUEUED: "QUEUED",
  PROCESSING: "PROCESSING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;

export const formProjectDetailsContent = (contents?: ProjectDetailsContent) => (
  <div>
    <span className={styles.title}>We are a</span>{" "}
    <span className={styles.header}>{contents?.tier1}</span>{" "}
    <span className={styles.title}>to a </span>
    <span className={styles.header}>{contents?.tier2}</span>{" "}
    <span className={styles.title}>on a </span>
    <span className={styles.header}>{contents?.tier3}</span>{" "}
    <span className={styles.title}>project</span>
  </div>
);

export const subSection1_titles: ProjectSummaryContent = {
  customer: "Customer",
  jobName: "Job Name",
  totalAmount: "Total Amount",
  totalOpenBalance: "Total Open Balance",
};

export const subSection2_titles: ProjectSummaryContent = {
  previousStage: "Previous stage",
  nextStage: "Next stage",
  filingDate: "Filing date",
  notifiedTo: "Notified to",
};

export const formProjectSummaryContent = (
  titles?: ProjectSummaryContent,
  contents?: ProjectSummaryContent,
) =>
  Object.keys(titles).map((key) => (
    <>
      <div className={styles.title}>{titles[key]}</div>
      <div className={styles.content}>
        {["totalAmount", "totalOpenBalance"].includes(key)
          ? `${getPrevDollorFormattedString(contents?.[key])}`
          : contents?.[key]}
      </div>
    </>
  ));

export const getColumns = (
  handleS3Links: () => void,
  editAccess: boolean,
): ColumnsType<LienDataType> => [
  {
    title: "Invoice Number",
    key: "invoiceNumber",
    align: "left",
    dataIndex: "invoiceNumber",
    render: (val) => <span className="table-font-mobile">{val}</span>,
  },
  {
    title: "Invoice Date",
    key: "invoiceDate",
    align: "left",
    dataIndex: "invoiceDate",
    render: (val) => <span className="table-font-mobile">{val}</span>,
  },
  {
    title: "Due Date",
    key: "dueDate",
    align: "left",
    dataIndex: "dueDate",
    render: (val) => <span className="table-font-mobile">{val}</span>,
  },
  {
    title: "Material Name",
    key: "materialName",
    align: "left",
    dataIndex: "materialName",
    render: (val) => <span className="table-font-mobile">{val}</span>,
  },
  {
    title: "Units",
    key: "units",
    align: "left",
    dataIndex: "units",
    render: (val) => <span className="table-font-mobile">{val}</span>,
  },
  {
    title: "Price",
    key: "price",
    align: "left",
    dataIndex: "price",
    render: (val) => (
      <span className="table-font-mobile">
        {getPrevDollorFormattedString(val)}
      </span>
    ),
  },
  {
    title: "Tax",
    key: "tax",
    align: "left",
    dataIndex: "tax",
    render: (val) => (
      <span className="table-font-mobile">
        {getPrevDollorFormattedString(val)}
      </span>
    ),
  },
  {
    title: "Amount",
    key: "amount",
    align: "left",
    dataIndex: "amount",
    render: (val) => (
      <span className="table-font-mobile">
        {getPrevDollorFormattedString(val)}
      </span>
    ),
  },
  {
    title: "Open Balance",
    key: "openBalance",
    align: "left",
    dataIndex: "openBalance",
    render: (val) => (
      <span className="table-font-mobile">
        {getPrevDollorFormattedString(val)}
      </span>
    ),
  },
  {
    title: "Ticket image",
    key: "ticketImage",
    align: "left",
    dataIndex: "ticketImage",
    render: (val) => {
      const imageName = val?.split("/")?.at(-1);
      return (
        <span className="table-font-mobile">
          <img src={jpegIcon} alt="icon" />
          {
            <a
              color="#1488FC"
              target="_blank"
              className={clsx({ [styles.downloadOnClickOption]: !editAccess })}
              onClick={() => {
                if (editAccess) handleS3Links(val);
                else message.error("Access required for download");
              }}
            >
              {imageName}
            </a>
          }
        </span>
      );
    },
  },
];
